import { useCallback, useEffect } from 'react';

import aguaCorporalTotalPng from '@assets/agua_corporal_total.png';
import sodioSericoCorrigidoPng from '@assets/sodio_serico_corrigido.png';
import { PageTitle } from '@components/PageTitle';
import { Button } from '@components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { Separator } from '@components/ui/separator';
import { EGender } from '@enums/Gender';
import formatterDecimal from '@formatters/decimal';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircleHelp } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { NumberFormatBase, NumericFormat } from 'react-number-format';
import { z } from 'zod';

import { useDisturbiosSodio } from '../context';
import { formSchema } from './formSchema';
import Hipernatremia from './Hipernatremia';
import Hiponatremia from './Hiponatremia';

function SodiumDisorder() {
  const {
    age,
    bloodGlucose,
    gender,
    serumSodium,
    serumSodiumFixed,
    setAge,
    setBloodGlucose,
    setGender,
    setSerumSodium,
    setWeight,
    water,
    weight,
  } = useDisturbiosSodio();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    shouldUnregister: true,
    defaultValues: {
      weight: weight.toFixed(1),
      age: String(age),
      gender: gender,
      serumSodium: serumSodium.toFixed(1),
      bloodGlucose: bloodGlucose.toFixed(1),
      mEq24h: (6).toFixed(1),
      solution1: String(0.45),
      solution2: String(10),
    },
  });

  const handleChangeWeight = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setWeight(valueParsed);
    },
    [setWeight],
  );

  const handleChangeAge = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setAge(valueParsed);
    },
    [setAge],
  );

  const handleChangeGender = useCallback(
    (value: EGender): void => {
      setGender(value);
    },
    [setGender],
  );

  const handleChangeSerumSodium = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setSerumSodium(valueParsed);
    },
    [setSerumSodium],
  );

  const handleChangeBloodGlucose = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setBloodGlucose(valueParsed);
    },
    [setBloodGlucose],
  );

  function onSubmit(): void {
    //
  }

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'weight' && value.weight) {
        handleChangeWeight(value.weight);
      }
      if (name === 'age' && value.age) {
        handleChangeAge(value.age);
      }
      if (name === 'gender' && value.gender) {
        handleChangeGender(value.gender);
      }
      if (name === 'serumSodium' && value.serumSodium) {
        handleChangeSerumSodium(value.serumSodium);
      }
      if (name === 'bloodGlucose' && value.bloodGlucose) {
        handleChangeBloodGlucose(value.bloodGlucose);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  useEffect(() => {
    setWeight(70);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <PageTitle hasBackButton title="Distúrbios do Sódio" />
      <Form {...form}>
        <form
          className="mx-auto max-w-3xl"
          onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mb-4 flex flex-row justify-center gap-4 sm:gap-6">
            <FormField
              control={form.control}
              name="weight"
              render={({ field: { ref, ...rest } }) => (
                <FormItem>
                  <FormLabel>Peso</FormLabel>
                  <FormControl>
                    <NumberFormatBase
                      className="[&_input]:w-26 [&_input]:text-center"
                      customInput={Input}
                      format={(value) => formatterDecimal(value, 1)}
                      getCaretBoundary={(formattedValue) => {
                        const boundaryAry = Array.from({
                          length: formattedValue.length + 1,
                        }).map(() => true);
                        boundaryAry[0] = false;
                        boundaryAry[1] = false;
                        boundaryAry[2] = false;
                        return boundaryAry;
                      }}
                      getInputRef={ref}
                      {...rest}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="age"
              render={({ field: { ref, ...rest } }) => (
                <FormItem>
                  <FormLabel>Idade</FormLabel>
                  <FormControl>
                    <NumericFormat
                      className="[&_input]:w-26 [&_input]:text-center"
                      customInput={Input}
                      getInputRef={ref}
                      {...rest}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sexo</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger onBlur={field.onBlur}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {[
                        {
                          label: 'Feminino',
                          value: EGender.Female,
                        },
                        {
                          label: 'Masculino',
                          value: EGender.Male,
                        },
                      ].map((item) => (
                        <SelectItem
                          className="focus:bg-tertiary"
                          key={item.value}
                          value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>

          <div className="mb-4 flex flex-row justify-center gap-8">
            <FormField
              control={form.control}
              name="serumSodium"
              render={({ field: { ref, ...rest } }) => (
                <FormItem>
                  <FormLabel>
                    Sódio sérico{' '}
                    <span className="text-xs font-light">(mEq/L)</span>
                  </FormLabel>
                  <FormControl>
                    <NumberFormatBase
                      className="[&_input]:w-36 [&_input]:text-center"
                      customInput={Input}
                      format={(value) => formatterDecimal(value, 1)}
                      getCaretBoundary={(formattedValue) => {
                        const boundaryAry = Array.from({
                          length: formattedValue.length + 1,
                        }).map(() => true);
                        boundaryAry[0] = false;
                        boundaryAry[1] = false;
                        boundaryAry[2] = false;
                        return boundaryAry;
                      }}
                      getInputRef={ref}
                      {...rest}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="bloodGlucose"
              render={({ field: { ref, ...rest } }) => (
                <FormItem>
                  <FormLabel>
                    Glicemia <span className="text-xs font-light">(mg/dL)</span>
                  </FormLabel>
                  <FormControl>
                    <NumberFormatBase
                      className="[&_input]:w-36 [&_input]:text-center"
                      customInput={Input}
                      format={(value) => formatterDecimal(value, 1)}
                      getCaretBoundary={(formattedValue) => {
                        const boundaryAry = Array.from({
                          length: formattedValue.length + 1,
                        }).map(() => true);
                        boundaryAry[0] = false;
                        boundaryAry[1] = false;
                        boundaryAry[2] = false;
                        return boundaryAry;
                      }}
                      getInputRef={ref}
                      {...rest}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-row justify-center gap-8">
            <div>
              <div className="flex flex-row items-center">
                <Label>Sódio sérico corrigido</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      className="cursor-pointer"
                      size="icon"
                      variant="ghost">
                      <CircleHelp className="text-foreground" strokeWidth={2} />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full max-w-lg space-y-4">
                    <h1 className="text-secondary text-center font-medium">
                      Sódio sérico corrigido
                    </h1>
                    <img
                      alt="Sódio sérico corrigido"
                      src={sodioSericoCorrigidoPng}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <p className="text-center">
                <span className="text-tertiary text-lg font-bold">
                  {serumSodiumFixed.toFixed(0)}
                </span>{' '}
              </p>
            </div>

            <div>
              <div className="flex flex-row items-center">
                <Label>Água corporal total</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      className="cursor-pointer"
                      size="icon"
                      variant="ghost">
                      <CircleHelp className="text-foreground" strokeWidth={2} />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full max-w-sm space-y-4 px-0">
                    <h1 className="text-secondary text-center font-medium">
                      Água corporal total
                    </h1>
                    <img
                      alt="Sódio sérico corrigido"
                      src={aguaCorporalTotalPng}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <p className="text-center">
                <span className="text-tertiary text-lg font-bold">
                  {water.toFixed(1)}
                </span>{' '}
              </p>
            </div>
          </div>
          <Separator className="my-4" />

          {serumSodiumFixed >= 135 && serumSodiumFixed <= 145 && (
            <div className="flex justify-center">
              <Label className="text-lg">
                Sódio sérico em níveis normais, sem necessidade de correção da
                natremia.
              </Label>
            </div>
          )}

          {serumSodiumFixed < 135 && <Hiponatremia />}

          {serumSodiumFixed > 145 && <Hipernatremia />}

          <p className="text-foreground mt-8 text-center text-xs font-light">
            Referências: Hyponatremia treatment guidelines 2007: expert panel
            recommendations. Am J Med 2007; 120:S1. / Hillier TA, Abbott RD,
            Barrett EJ. Hyponatremia: Evaluating the Correction Factor for
            Hyperglycemia.
          </p>
        </form>
      </Form>
    </div>
  );
}
export default SodiumDisorder;
